import { useState, useEffect } from 'react';
import { X, Save, Loader2, Trash2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useListenStore } from '../../stores/listenStore';
import { authApi } from '../../api/auth';

interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Settings({ isOpen, onClose }: SettingsProps) {
  const { user, setUser } = useAuthStore();
  const { clearHistory } = useListenStore();
  const [crossfadeDuration, setCrossfadeDuration] = useState(500);
  const [audioVolume, setAudioVolume] = useState(0.7);
  const [listenHistoryEnabled, setListenHistoryEnabled] = useState(true);
  const [newSongBorderColor, setNewSongBorderColor] = useState('#7ba3d6');
  const [showVoteRanks, setShowVoteRanks] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Initialize from user preferences
  useEffect(() => {
    if (user?.preferences) {
      setCrossfadeDuration(user.preferences.crossfadeDurationMs ?? 500);
      setAudioVolume(user.preferences.audioVolume ?? 0.7);
      setListenHistoryEnabled(user.preferences.listenHistoryEnabled !== false);
      setNewSongBorderColor(user.preferences.newSongBorderColor || '#7ba3d6');
      setShowVoteRanks(user.preferences.showVoteRanks !== false);
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      const result = await authApi.updatePreferences({
        crossfadeDurationMs: crossfadeDuration,
        audioVolume: audioVolume,
        listenHistoryEnabled: listenHistoryEnabled,
        newSongBorderColor: newSongBorderColor,
        showVoteRanks: showVoteRanks,
      });

      // Update user in store
      if (user) {
        setUser({
          ...user,
          preferences: result.preferences,
        });
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      setSaveError((error as Error).message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetHistory = async () => {
    if (!confirm('Are you sure you want to reset your listen history? This will mark all songs as new again.')) {
      return;
    }

    setResetting(true);
    setResetSuccess(false);

    try {
      await clearHistory();
      setResetSuccess(true);
      setTimeout(() => setResetSuccess(false), 2000);
    } catch (error) {
      setSaveError('Failed to reset listen history');
    } finally {
      setResetting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Settings Panel */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-96 bg-monster-card border-l border-monster-border z-50 overflow-y-auto slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-monster-card border-b border-monster-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Settings</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Audio Settings Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-monster-blue">Audio</h3>

            {/* Default Volume */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center justify-between">
                <label htmlFor="volume" className="text-sm font-medium text-gray-300">
                  Default Volume
                </label>
                <span className="text-sm text-gray-400">{Math.round(audioVolume * 100)}%</span>
              </div>
              <input
                id="volume"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={audioVolume}
                onChange={(e) => setAudioVolume(Number(e.target.value))}
                className="w-full h-2 bg-monster-dark rounded-lg appearance-none cursor-pointer accent-monster-blue"
              />
              <p className="text-xs text-gray-500">
                Default playback volume for new songs
              </p>
            </div>

            {/* Crossfade Duration */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="crossfade" className="text-sm font-medium text-gray-300">
                  Crossfade Duration
                </label>
                <span className="text-sm text-gray-400">{crossfadeDuration}ms</span>
              </div>
              <input
                id="crossfade"
                type="range"
                min="0"
                max="2000"
                step="100"
                value={crossfadeDuration}
                onChange={(e) => setCrossfadeDuration(Number(e.target.value))}
                className="w-full h-2 bg-monster-dark rounded-lg appearance-none cursor-pointer accent-monster-blue"
              />
              <p className="text-xs text-gray-500">
                {crossfadeDuration === 0
                  ? 'No crossfade (instant switch)'
                  : `Smooth ${crossfadeDuration}ms transition between songs`}
              </p>
            </div>
          </div>

          {/* Voting Settings Section */}
          <div className="pt-4 border-t border-monster-border">
            <h3 className="text-lg font-semibold mb-4 text-monster-blue">Voting</h3>

            {/* Show Vote Ranks Toggle */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center justify-between">
                <label htmlFor="showVoteRanks" className="text-sm font-medium text-gray-300">
                  Show Vote Ranks on Cards
                </label>
                <button
                  type="button"
                  onClick={() => setShowVoteRanks(!showVoteRanks)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    showVoteRanks ? 'bg-monster-blue' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                      showVoteRanks ? 'left-[22px]' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Display your vote rank on song cards in the grid
              </p>
            </div>
          </div>

          {/* Listen History Settings Section */}
          <div className="pt-4 border-t border-monster-border">
            <h3 className="text-lg font-semibold mb-4 text-monster-blue">Listen History</h3>

            {/* Enable/Disable Toggle */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center justify-between">
                <label htmlFor="listenHistory" className="text-sm font-medium text-gray-300">
                  Track Listened Songs
                </label>
                <button
                  type="button"
                  onClick={() => setListenHistoryEnabled(!listenHistoryEnabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    listenHistoryEnabled ? 'bg-monster-blue' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                      listenHistoryEnabled ? 'left-[22px]' : 'left-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Show a subtle border on songs you haven't listened to yet
              </p>
            </div>

            {/* Border Color Picker */}
            {listenHistoryEnabled && (
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between">
                  <label htmlFor="borderColor" className="text-sm font-medium text-gray-300">
                    New Song Border Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="borderColor"
                      type="color"
                      value={newSongBorderColor}
                      onChange={(e) => setNewSongBorderColor(e.target.value)}
                      className="w-10 h-8 rounded cursor-pointer bg-monster-dark border border-monster-border"
                    />
                    <span className="text-sm text-gray-400 font-mono">{newSongBorderColor}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Choose the color for highlighting songs you haven't heard
                </p>
              </div>
            )}

            {/* Reset History Button */}
            {listenHistoryEnabled && (
              <div className="space-y-2">
                <button
                  onClick={handleResetHistory}
                  disabled={resetting}
                  className="w-full btn-secondary flex items-center justify-center gap-2 text-monster-crimson hover:bg-monster-crimson/10"
                >
                  {resetting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={18} />
                      Reset Listen History
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500">
                  Mark all songs as new (cannot be undone)
                </p>
                {resetSuccess && (
                  <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-sm text-green-500">
                    Listen history reset successfully!
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Settings
                </>
              )}
            </button>

            {/* Success Message */}
            {saveSuccess && (
              <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded text-sm text-green-500">
                Settings saved successfully!
              </div>
            )}

            {/* Error Message */}
            {saveError && (
              <div className="mt-3 p-3 bg-monster-crimson/10 border border-monster-crimson/30 rounded text-sm text-monster-crimson">
                {saveError}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
